/**
 * "Nutzung & Kosten" — der Heimatbildschirm für Guthaben + Verbrauch.
 *
 * Render-Strategie: STRUKTUR ZUERST, INHALT ASYNC.
 *  Vorher wartete die Seite auf zwei React-Query-Hooks, bevor irgendetwas
 *  gerendert wurde — das ergab eine 1-2 Sekunden weiße Seite. Jetzt
 *  rendern wir alle Karten sofort mit Skeleton-Platzhaltern und füllen,
 *  sobald die Daten eintreffen.
 *
 * Sichtbarkeits-Regeln (per `settingsMode`):
 *   - Anfänger:  KPI-Tiles (Kosten + Minuten + Anrufe) + Wallet + Rechnungen
 *   - Fortgeschr.: zusätzlich Latenz-KPI + Monatsverlauf + 12-Mo-Chart + Letzte Anrufe
 */
import * as React from 'react';
import { Link } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  ChevronDown,
  ChevronRight,
  Coins,
  Loader2,
  Phone,
  Timer,
  Zap,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { useMyUsage } from '../hooks/useAdminUsage';
import { useMyAdvancedAccess, useMyBilling, useSetSpendCap } from '../hooks/useBilling';
import { useUser } from '../auth/AuthProvider';
import { useToast } from '../components/ui/toast';
import {
  AutoTopUpCard,
  BalanceCard,
  CancelCard,
  InvoicesListCard,
  PaymentMethodsCard,
  PlanSwitchCard,
} from '../components/billing/WalletCards';
import { cn } from '../lib/utils';

export function MyUsagePage() {
  // Both queries fire in parallel; we render eagerly and degrade per-card.
  const detail = useMyUsage();
  const billing = useMyBilling();
  const advanced = useMyAdvancedAccess();
  const { profile, isAdmin } = useUser();

  const isAdvanced = billing.data?.settingsMode === 'advanced';
  // The wallet-bound billing UI applies to anyone on a Pro/Enterprise/
  // Ultimate Mollie plan — i.e. the underlying anfaenger_* billing
  // record is `active`. We deliberately drop the previous `!isAdvanced`
  // gate so Enterprise + Ultimate customers see the same wallet view
  // as Pro: identical layout, same payment-methods/invoices/auto-topup
  // controls, just whatever quota / per-minute price the plan dictates.
  const hasActiveBilling =
    !!billing.data &&
    billing.data.billing.status === 'active' &&
    !!billing.data.billing.mode;
  // Latency tile is reserved for Enterprise+ (they care about the
  // technical KPIs); kept on `settingsMode === 'advanced'` so it
  // tracks the toggle the user picked rather than the plan tier.
  const showLatency = isAdvanced || advanced.data?.access.status === 'active';
  // Monatsverlauf + Kostenverlauf charts: admin OR ultimate-tier ONLY.
  // Pro / Enterprise customers don't see these — explicit user request.
  const myTier = profile?.membership?.tier ?? null;
  const showHistoryCharts =
    isAdmin
    || myTier === 'ultimate'
    || advanced.data?.access.tier === 'ultimate';

  return (
    <div className="space-y-6">
      {/* ── KPI tiles — always rendered, content swaps when ready ── */}
      <KpiTiles
        usage={detail.data ?? null}
        showLatency={showLatency}
        loading={detail.isLoading}
      />

      {/* ── Wallet zone — visible for ALL paying tiers ────────────
          Render the structure as soon as the billing flag is known so
          there's no layout shift. */}
      {billing.isLoading ? (
        <WalletSkeleton />
      ) : hasActiveBilling && billing.data ? (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <ForecastCard
              billing={billing.data.billing}
              usage={detail.data ?? null}
            />
            <SpendCapCard billing={billing.data.billing} />
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <BalanceCard data={billing.data} />
            <PaymentMethodsCard data={billing.data} />
            <AutoTopUpCard data={billing.data} />
            <InvoicesListCard />
            <PlanSwitchCard data={billing.data} />
            <CancelCard data={billing.data} />
          </div>
        </>
      ) : null}

      {/* ── Advanced charts (Admin + Ultimate only) ─────────────
          The Monatsverlauf-Tab card + Jahres-Kostenverlauf-Chart used
          to render for every advanced-mode user. Per explicit
          requirement they're now reserved for admins + ultimate-tier
          customers — Pro and Enterprise see only the wallet zone and
          the KPI tiles above. */}
      {showHistoryCharts && (
        <>
          <div className="grid gap-4 lg:grid-cols-2">
            <MonthlyTabsCard data={detail.data ?? null} loading={detail.isLoading} />

            <CollapsibleCard
              title="Deine Kosten im Jahresverlauf"
              description="Übersichtlich aufgeteilt nach Telefonie, KI-Modell, Stimme und Spracherkennung. Aktuelle Tarife seit dem 1. April 2026."
              loading={detail.isLoading}
            >
              {detail.data && <CostHistoryChart data={detail.data} />}
            </CollapsibleCard>
          </div>
        </>
      )}

      {detail.isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 p-3 text-sm text-destructive">
          Verbrauchsdaten konnten nicht geladen werden:{' '}
          {(detail.error as Error)?.message ?? 'Unbekannt'}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Usage-Forecast + Spend-Cap (above the wallet-zone)
// ─────────────────────────────────────────────────────────────────────

/**
 * Linear-extrapolation card. Takes the user's spend-so-far this period
 * and projects month-end based on velocity (days elapsed / days in
 * period). Useful for any plan but most meaningful for PAYG and
 * Subscription-with-overage where the customer's exposure isn't
 * capped by the plan price alone.
 */
function ForecastCard({
  billing,
  usage,
}: {
  billing: ReturnType<typeof useMyBilling>['data'] extends infer T
    ? T extends { billing: infer B }
      ? B
      : never
    : never;
  usage: NonNullable<ReturnType<typeof useMyUsage>['data']> | null;
}) {
  const now = Date.now();
  const renewalAtMs =
    (billing as { subscriptionRenewalAtMs?: number | null }).subscriptionRenewalAtMs ?? null;
  // For PAYG (no renewal anchor) project against calendar month end so
  // the user gets a meaningful "by month-end" number.
  const periodStart = (() => {
    if (renewalAtMs) return renewalAtMs - 30 * 24 * 60 * 60 * 1000;
    const d = new Date();
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), 1);
  })();
  const periodEnd = renewalAtMs
    ?? (() => {
      const d = new Date();
      return Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 1);
    })();
  const daysElapsed = Math.max(1, (now - periodStart) / 86_400_000);
  const totalDays = Math.max(1, (periodEnd - periodStart) / 86_400_000);
  const remainingDays = Math.max(0, totalDays - daysElapsed);

  const spendCents =
    (billing as { periodSpendCents?: number }).periodSpendCents ?? 0;
  const projectedCents = Math.round((spendCents / daysElapsed) * totalDays);
  const projectedRemainingCents = Math.max(0, projectedCents - spendCents);

  const usedMin =
    (billing as { subscriptionMonthlyUsedMinutes?: number }).subscriptionMonthlyUsedMinutes
    ?? 0;
  const allottedMin =
    (billing as { subscriptionMonthlyMinutes?: number }).subscriptionMonthlyMinutes ?? 0;
  const projectedMin = Math.round((usedMin / daysElapsed) * totalDays);
  const overageMin = Math.max(0, projectedMin - allottedMin);

  // Pull the most-recent month's call-count + minutes for a sanity hint.
  const recentCalls = usage?.history[0]?.callCount ?? 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Prognose</CardTitle>
        <CardDescription>
          Bei deinem aktuellen Tempo. Periode endet in{' '}
          <strong>{remainingDays.toFixed(0)} Tagen</strong>.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-2 sm:grid-cols-2">
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Bisher diese Periode
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {(spendCents / 100).toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })}
            </div>
            <div className="text-[11px] text-muted-foreground">
              {recentCalls} Anrufe · {usedMin.toFixed(1)} Min
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted-foreground">
              Projektion bis Periodenende
            </div>
            <div className="text-2xl font-semibold tabular-nums">
              {(projectedCents / 100).toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })}
            </div>
            <div className="text-[11px] text-muted-foreground">
              davon noch{' '}
              {(projectedRemainingCents / 100).toLocaleString('de-DE', {
                style: 'currency',
                currency: 'EUR',
              })}
              {overageMin > 0 && (
                <> · {overageMin.toFixed(0)} Min Überlauf</>
              )}
            </div>
          </div>
        </div>
        {overageMin > 0 && (
          <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 text-xs text-amber-200">
            Du wirst voraussichtlich <strong>{overageMin.toFixed(0)} Minuten</strong>{' '}
            über deinem inkludierten Kontingent landen. Setze ggf. einen Spend-Cap
            rechts daneben.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * User-controlled spend cap. When set, the voice agent rejects new
 * calls once `periodSpendCents` reaches the limit. Reset on Mollie
 * renewal. Setting `null` lifts the cap.
 */
function SpendCapCard({
  billing,
}: {
  billing: ReturnType<typeof useMyBilling>['data'] extends infer T
    ? T extends { billing: infer B }
      ? B
      : never
    : never;
}) {
  const setCap = useSetSpendCap();
  const { toast } = useToast();
  const initialCents = (billing as { spendCapCents?: number | null }).spendCapCents ?? null;
  const [draft, setDraft] = React.useState(
    initialCents != null ? (initialCents / 100).toFixed(2) : '',
  );

  React.useEffect(() => {
    setDraft(initialCents != null ? (initialCents / 100).toFixed(2) : '');
  }, [initialCents]);

  async function onSave(target: number | null) {
    try {
      await setCap.mutateAsync(target);
      toast({
        title: target == null ? 'Spend-Cap entfernt' : 'Spend-Cap gespeichert',
        description:
          target == null
            ? 'Es gibt keine harte Obergrenze mehr für deine monatlichen Kosten.'
            : `Sobald du in dieser Periode ${(target / 100).toFixed(2)} € erreichst, blockt die KI neue Anrufe.`,
      });
    } catch (err) {
      toast({
        title: 'Fehler',
        description: (err as Error).message,
        variant: 'destructive',
      });
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Ausgabenlimit</CardTitle>
        <CardDescription>
          Optionale harte Obergrenze pro Abrechnungsperiode. Sobald
          erreicht, lehnt der Voice-Agent neue Anrufe ab — verbleibende
          inkludierte Minuten bleiben verfügbar.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground" htmlFor="spend-cap">
              Limit in Euro
            </label>
            <input
              id="spend-cap"
              type="number"
              min="0"
              step="0.01"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="z.B. 200"
              className="mt-1 h-9 w-full rounded-md border border-border bg-background px-3 text-sm tabular-nums"
            />
          </div>
          <Button
            size="sm"
            onClick={() => {
              const num = parseFloat(draft.replace(',', '.'));
              if (!Number.isFinite(num) || num <= 0) {
                void onSave(null);
              } else {
                void onSave(Math.round(num * 100));
              }
            }}
            disabled={setCap.isPending}
          >
            {setCap.isPending ? <Loader2 className="mr-2 size-3.5 animate-spin" /> : null}
            Speichern
          </Button>
          {initialCents != null && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => void onSave(null)}
              disabled={setCap.isPending}
            >
              Aufheben
            </Button>
          )}
        </div>
        {initialCents != null && (
          <div className="text-xs text-muted-foreground">
            Aktiv: <strong>{(initialCents / 100).toFixed(2)} €</strong> pro Periode.
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// KPI tiles
// ─────────────────────────────────────────────────────────────────────

function KpiTiles({
  usage,
  showLatency,
  loading,
}: {
  usage: NonNullable<ReturnType<typeof useMyUsage>['data']> | null;
  showLatency: boolean;
  loading: boolean;
}) {
  const currentMonth = usage?.history[0];
  const total12mCost = usage?.history.reduce((a, m) => a + m.estimatedCostUsd, 0) ?? 0;
  const total12mMinutes = usage?.history.reduce((a, m) => a + m.totalMinutes, 0) ?? 0;
  const total12mCalls = usage?.history.reduce((a, m) => a + m.callCount, 0) ?? 0;

  return (
    <div
      className={cn(
        'grid gap-3 sm:grid-cols-2',
        showLatency ? 'lg:grid-cols-4' : 'lg:grid-cols-3',
      )}
    >
      <SummaryCard
        icon={<Coins className="h-4 w-4" />}
        label={`Deine Kosten im ${monthLabel(currentMonth?.yearMonth)}`}
        value={loading ? null : formatUsd(currentMonth?.estimatedCostUsd ?? 0)}
        hint={loading ? null : `${formatUsd(total12mCost)} Ausgaben der letzten zwölf Monate`}
      />
      <SummaryCard
        icon={<Timer className="h-4 w-4" />}
        label="Gesprächsminuten in diesem Monat"
        value={
          loading
            ? null
            : (currentMonth?.totalMinutes ?? 0).toLocaleString('de-DE', {
                maximumFractionDigits: 1,
              })
        }
        hint={
          loading
            ? null
            : `Im Schnitt ${(total12mMinutes / Math.max(1, usage?.history.length ?? 1)).toLocaleString(
                'de-DE',
                { maximumFractionDigits: 0 },
              )} pro Monat`
        }
      />
      <SummaryCard
        icon={<Phone className="h-4 w-4" />}
        label="Anrufe in diesem Monat"
        value={loading ? null : (currentMonth?.callCount ?? 0).toString()}
        hint={loading ? null : `${total12mCalls} Anrufe in den letzten zwölf Monaten`}
      />
      {showLatency && (
        <SummaryCard
          icon={<Zap className="h-4 w-4" />}
          label="Antwortgeschwindigkeit deiner KI"
          value={
            loading
              ? null
              : `${currentMonth?.avgLatencyP50Ms ?? 0} / ${currentMonth?.avgLatencyP95Ms ?? 0} ms`
          }
          hint={
            loading
              ? null
              : currentMonth?.topModel
                ? `Top-Modell: ${currentMonth.topModel}`
                : 'Bald siehst du hier deine ersten Werte'
          }
        />
      )}
    </div>
  );
}

function SummaryCard({
  icon,
  label,
  value,
  hint,
}: {
  icon: React.ReactNode;
  label: string;
  value: string | null;
  hint?: string | null;
}) {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardDescription>{label}</CardDescription>
        <span className="text-muted-foreground">{icon}</span>
      </CardHeader>
      <CardContent>
        {value === null ? (
          <Skeleton className="h-7 w-24" />
        ) : (
          <div className="text-2xl font-semibold tabular-nums">{value}</div>
        )}
        {hint === null ? (
          <Skeleton className="mt-1 h-3 w-32" />
        ) : (
          hint && <div className="mt-1 text-xs text-muted-foreground">{hint}</div>
        )}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Monatsverlauf + Letzte Anrufe als Tabs in einer Karte
// ─────────────────────────────────────────────────────────────────────

function MonthlyTabsCard({
  data,
  loading,
}: {
  data: NonNullable<ReturnType<typeof useMyUsage>['data']> | null;
  loading: boolean;
}) {
  const [tab, setTab] = React.useState<'overview' | 'recent'>('overview');
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Dein Monatsverlauf</CardTitle>
        <CardDescription>
          {tab === 'overview'
            ? 'So entwickeln sich deine Anrufe, Minuten und Kosten von Monat zu Monat. Der aktuelle Monat aktualisiert sich live für dich.'
            : 'Für jeden Anruf siehst du Dauer, verwendete Technik und die voraussichtlichen Kosten.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="inline-flex rounded-md border border-border bg-muted/30 p-0.5 text-xs">
          <button
            type="button"
            onClick={() => setTab('overview')}
            className={cn(
              'rounded px-3 py-1.5 transition-colors',
              tab === 'overview'
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Übersicht
          </button>
          <button
            type="button"
            onClick={() => setTab('recent')}
            className={cn(
              'rounded px-3 py-1.5 transition-colors',
              tab === 'recent'
                ? 'bg-background font-medium text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            Letzte Anrufe
          </button>
        </div>
        {loading ? (
          <div className="flex h-32 items-center justify-center text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
          </div>
        ) : data ? (
          <div className="max-h-96 overflow-y-auto">
            {tab === 'overview' ? (
              <MonthlyTable data={data} />
            ) : (
              <RecentCallsTable data={data} />
            )}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Wallet skeleton
// ─────────────────────────────────────────────────────────────────────

function WalletSkeleton() {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      {Array.from({ length: 4 }).map((_, i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-5 w-32" />
            <Skeleton className="mt-2 h-3 w-48" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-9 w-32" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Collapsible info cards (advanced only)
// ─────────────────────────────────────────────────────────────────────

function CollapsibleCard({
  title,
  description,
  defaultOpen = false,
  loading,
  children,
}: {
  title: string;
  description?: string;
  defaultOpen?: boolean;
  loading?: boolean;
  children: React.ReactNode;
}) {
  const [open, setOpen] = React.useState(defaultOpen);
  return (
    <Card>
      <CardHeader className="cursor-pointer select-none" onClick={() => setOpen((o) => !o)}>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-base">{title}</CardTitle>
            {description && (
              <CardDescription className="mt-1">{description}</CardDescription>
            )}
          </div>
          {open ? (
            <ChevronDown className="h-4 w-4 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-4 w-4 text-muted-foreground" />
          )}
        </div>
      </CardHeader>
      {open && (
        <CardContent>
          {loading ? (
            <div className="flex h-32 items-center justify-center text-muted-foreground">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Lade …
            </div>
          ) : (
            children
          )}
        </CardContent>
      )}
    </Card>
  );
}

function MonthlyTable({
  data,
}: {
  data: NonNullable<ReturnType<typeof useMyUsage>['data']>;
}) {
  const currentMonth = data.history[0];
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Monat</TableHead>
          <TableHead className="text-right">Anrufe</TableHead>
          <TableHead className="text-right">Min</TableHead>
          <TableHead className="text-right">Latenz P50/P95</TableHead>
          <TableHead className="text-right">Gesamt</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {[...data.history].map((m) => (
          <TableRow
            key={m.yearMonth}
            className={cn(m.yearMonth === currentMonth?.yearMonth && 'bg-muted/40')}
          >
            <TableCell className="font-medium">
              {monthLabel(m.yearMonth)}
              {m.yearMonth === currentMonth?.yearMonth && (
                <Badge variant="outline" className="ml-2">
                  aktuell
                </Badge>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">{m.callCount}</TableCell>
            <TableCell className="text-right tabular-nums">
              {m.totalMinutes.toLocaleString('de-DE', { maximumFractionDigits: 1 })}
            </TableCell>
            <TableCell className="text-right tabular-nums text-xs text-muted-foreground">
              {m.avgLatencyP50Ms} / {m.avgLatencyP95Ms} ms
            </TableCell>
            <TableCell className="text-right tabular-nums font-medium">
              {formatUsd(m.estimatedCostUsd)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function CostHistoryChart({
  data,
}: {
  data: NonNullable<ReturnType<typeof useMyUsage>['data']>;
}) {
  const history = [...data.history].sort((a, b) => a.yearMonth.localeCompare(b.yearMonth));
  const currentMonth = data.history[0];
  const chartData = history.map((m) => ({
    label: shortMonthLabel(m.yearMonth),
    yearMonth: m.yearMonth,
    Telefonie: round2(m.breakdown.telephonyUsd),
    LLM: round2(m.breakdown.llmUsd),
    TTS: round2(m.breakdown.ttsUsd),
    STT: round2(m.breakdown.sttUsd),
    cost: m.estimatedCostUsd,
    isCurrent: m.yearMonth === currentMonth?.yearMonth,
  }));
  return (
    <div className="h-72 w-full">
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
          <XAxis
            dataKey="label"
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
          />
          <YAxis
            tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }}
            tickLine={false}
            axisLine={false}
            tickFormatter={(v) => `$${v}`}
          />
          <Tooltip
            contentStyle={{
              backgroundColor: 'hsl(var(--popover))',
              border: '1px solid hsl(var(--border))',
              borderRadius: '8px',
              fontSize: '12px',
            }}
            formatter={(value: number, name: string) => [formatUsd(value), name]}
          />
          <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} iconType="circle" iconSize={8} />
          <Bar dataKey="Telefonie" stackId="cost" fill="#0ea5e9" />
          <Bar dataKey="LLM" stackId="cost" fill="#8b5cf6" />
          <Bar dataKey="TTS" stackId="cost" fill="#10b981" />
          <Bar dataKey="STT" stackId="cost" fill="#f59e0b">
            {chartData.map((d, i) => (
              <Cell key={i} fillOpacity={d.isCurrent ? 1 : 0.85} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

/**
 * Renders the per-call cost summary as a compact total with a hover-
 * tooltip that breaks the number down into the four cost buckets the
 * voice-agent emits at call end (Telephony, LLM, STT, TTS). Numbers
 * already exist in the call doc — we just surface them so the user
 * can see WHERE their money goes per call.
 */
function CostBreakdownCell({ cost }: { cost: { totalUsd: number; telephonyUsd?: number; llmUsd?: number; sttUsd?: number; ttsUsd?: number } }) {
  const has =
    (cost.telephonyUsd ?? 0) > 0
    || (cost.llmUsd ?? 0) > 0
    || (cost.sttUsd ?? 0) > 0
    || (cost.ttsUsd ?? 0) > 0;
  if (!has) {
    return <span>{formatUsd(cost.totalUsd)}</span>;
  }
  const tooltip = [
    ['Telefonie', cost.telephonyUsd ?? 0],
    ['LLM', cost.llmUsd ?? 0],
    ['STT', cost.sttUsd ?? 0],
    ['TTS', cost.ttsUsd ?? 0],
  ]
    .map(([k, v]) => `${k}: ${formatUsd(v as number)}`)
    .join(' · ');
  return (
    <span title={tooltip} className="cursor-help underline decoration-dotted underline-offset-2">
      {formatUsd(cost.totalUsd)}
    </span>
  );
}

function RecentCallsTable({
  data,
}: {
  data: NonNullable<ReturnType<typeof useMyUsage>['data']>;
}) {
  if (data.recent.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        Noch keine Anrufe.
      </div>
    );
  }
  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Zeitpunkt</TableHead>
          <TableHead>Anrufer</TableHead>
          <TableHead>Provider</TableHead>
          <TableHead className="text-right">Dauer</TableHead>
          <TableHead className="text-right">Latenz P95</TableHead>
          <TableHead>Modell</TableHead>
          <TableHead className="text-right">Kosten</TableHead>
          <TableHead></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.recent.map((c) => (
          <TableRow key={c.id}>
            <TableCell className="text-xs">
              {c.startedAt
                ? new Date(c.startedAt).toLocaleString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: '2-digit',
                    hour: '2-digit',
                    minute: '2-digit',
                  })
                : '—'}
            </TableCell>
            <TableCell className="text-xs font-mono">{c.callerNumber ?? '—'}</TableCell>
            <TableCell>
              {c.provider ? (
                <Badge variant="outline">{c.provider}</Badge>
              ) : (
                <span className="text-xs text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              {formatDuration(c.durationSec)}
            </TableCell>
            <TableCell className="text-right tabular-nums text-xs">
              {c.latencyP95 ? `${c.latencyP95} ms` : '—'}
            </TableCell>
            <TableCell className="text-xs">
              {c.llmProviderUsed && c.llmModelUsed
                ? `${c.llmProviderUsed}/${c.llmModelUsed}`
                : '—'}
            </TableCell>
            <TableCell className="text-right tabular-nums">
              <CostBreakdownCell cost={c.cost} />
            </TableCell>
            <TableCell>
              <Button asChild variant="ghost" size="sm" className="h-7 text-xs">
                <Link to={`/settings/calls/${c.id}`}>Details</Link>
              </Button>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────

function monthLabel(yearMonth: string | undefined): string {
  if (!yearMonth) return '—';
  const [y, m] = yearMonth.split('-').map(Number);
  if (!y || !m) return yearMonth;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
    month: 'long',
    year: 'numeric',
  });
}

function shortMonthLabel(yearMonth: string): string {
  const [y, m] = yearMonth.split('-').map(Number);
  if (!y || !m) return yearMonth;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('de-DE', {
    month: 'short',
  });
}

function formatUsd(amount: number): string {
  return amount.toLocaleString('de-DE', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDuration(sec: number): string {
  if (!sec || sec < 0) return '—';
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}
